import type { ReactNode } from 'react';

/**
 * Shared empty / error / skeleton states. Each is wrapped in the platform
 * Card shell so the widget keeps a consistent silhouette regardless of which
 * branch renders.
 */

export function StateCard({
  tone = 'muted',
  eyebrow,
  title,
  children,
}: {
  tone?: 'muted' | 'warning' | 'danger';
  eyebrow: string;
  title: string;
  children?: ReactNode;
}) {
  const eyebrowColor = tone === 'warning' ? 'text-warning' : tone === 'danger' ? 'text-danger' : 'text-muted';
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className="bg-surface border border-border rounded-card shadow-sm px-5 py-6"
    >
      <div className={`text-[10px] uppercase tracking-wider font-medium mb-1.5 ${eyebrowColor}`}>{eyebrow}</div>
      <div className="text-sm text-primary font-medium mb-1">{title}</div>
      {children && <div className="text-xs text-secondary leading-relaxed mt-2">{children}</div>}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <StateCard eyebrow="No data" title={title}>
      {hint}
    </StateCard>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <StateCard tone="danger" eyebrow="Widget error" title="Could not render this widget.">
      <code className="font-mono text-[11px] text-secondary break-words">{message}</code>
    </StateCard>
  );
}

/**
 * Skeleton — visual placeholder while host bridge populates `data`. We keep
 * the silhouette matched to the real widget so first paint doesn't shift
 * the layout.
 */
export function Skeleton({ shape }: { shape: 'leaderboard' | 'brand-stats' }) {
  if (shape === 'leaderboard') {
    return (
      <div
        aria-busy="true"
        aria-live="polite"
        className="bg-surface border border-border rounded-card shadow-sm overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-border">
          <div className="h-3 w-32 bg-surface-hover rounded animate-pulse" />
          <div className="mt-2 h-2 w-48 bg-surface-hover rounded animate-pulse opacity-70" />
        </div>
        <div className="px-5 py-4 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-2 w-6 bg-surface-hover rounded animate-pulse" />
              <div className="h-3 bg-surface-hover rounded animate-pulse flex-1" style={{ width: `${85 - i * 10}%` }} />
              <div className="h-2 w-10 bg-surface-hover rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className="bg-surface border border-border rounded-card shadow-sm overflow-hidden"
    >
      <div className="px-5 py-4 border-b border-border">
        <div className="h-3 w-28 bg-surface-hover rounded animate-pulse" />
        <div className="mt-2 h-2 w-40 bg-surface-hover rounded animate-pulse opacity-70" />
      </div>
      <div className="px-5 py-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 bg-surface-hover rounded animate-pulse" />
          ))}
        </div>
        <div className="h-32 bg-surface-hover rounded animate-pulse" />
      </div>
    </div>
  );
}
